import React, { PureComponent } from 'react';

import {
	ContextualSurvey as AkContextualSurvey,
	DismissTrigger,
	type OnDismissArgs,
} from '@atlaskit/contextual-survey';

import { type SeenMessageConfig } from '../../services/engage-targeting';

import { type FormValues } from './types';

type Props = {
	question: string;
	statement: string;
	textPlaceholder?: string;
	fireClickEvent: (event: string) => void;
	aaid: string;
	cloudId: string;
	stargateUrl: string;
	messageId: string;
	variationId: string;
	componentId: number;
	product: string;
	orgId: string;
	workspaceId: string;
	markAsSeenAndFatigued: (seenMessageConfig: SeenMessageConfig) => void;
	sendUserResponse: (
		cloudId: string,
		stargateUrl: string,
		messageId: string,
		payload: any,
	) => Promise<void>;
	getFlag: (stargateUrl: string, cloudId: string, flagKey: string, aaid: string) => void;
	setFlag: (stargateUrl: string, cloudId: string, flagKey: string, userKey: string) => void;
};

const flagKey = 'atlassian_research_group_mailinglist_actioned';

// eslint-disable-next-line @repo/internal/react/no-class-components
export default class ContextualSurvey extends PureComponent<Props> {
	firePrimaryClickEvent() {
		this.props.fireClickEvent('primary');
	}

	onDismiss = (onDismissArgs: OnDismissArgs) => {
		const {
			cloudId,
			messageId,
			fireClickEvent,
			stargateUrl,
			markAsSeenAndFatigued,
			orgId,
			workspaceId,
		} = this.props;

		if (onDismissArgs.trigger === DismissTrigger.Manual) {
			markAsSeenAndFatigued({
				stargateUrl,
				cloudId,
				messageId,
				orgId,
				workspaceId,
			});
		}
		fireClickEvent('dismiss');
	};

	getUserHasAnsweredMailingList = async () => {
		const { stargateUrl, aaid, cloudId, getFlag } = this.props;
		try {
			await getFlag(stargateUrl, cloudId, flagKey, aaid);

			return Promise.resolve(false);
		} catch {
			return Promise.resolve(true);
		}
	};

	onSubmit = async (formValues: FormValues) => {
		const {
			cloudId,
			messageId,
			question,
			statement,
			stargateUrl,
			markAsSeenAndFatigued,
			sendUserResponse,
			orgId,
			workspaceId,
		} = this.props;
		const { canContact, feedbackScore, writtenFeedback } = formValues;
		const payload = {
			responseType: 'ACCEPTANCE_SURVEY',
			properties: {
				surveyTimestamp: Date.now(),
				contextualQuestion: question,
				contextualStatement: statement,
				quantitativeResponse: feedbackScore,
				qualitativeResponse: writtenFeedback,
				contactable: canContact,
			},
		};

		markAsSeenAndFatigued({
			stargateUrl,
			cloudId,
			messageId,
			orgId,
			workspaceId,
		});

		this.firePrimaryClickEvent();

		// Resolving the promise to ensure that spinner of Submit button does not spin forever in case of errors
		return sendUserResponse(cloudId, stargateUrl, messageId, payload).catch(() =>
			Promise.resolve(),
		);
	};

	onMailingListAnswer = async (answer: boolean) => {
		const { aaid, cloudId, stargateUrl, messageId, sendUserResponse, setFlag } = this.props;
		const payload = {
			responseType: 'MAILING_LIST_RESPONSE',
			properties: {},
		};
		setFlag(stargateUrl, cloudId, flagKey, aaid);

		if (!answer) {
			return;
		}

		return sendUserResponse(cloudId, stargateUrl, messageId, payload);
	};

	render(): React.JSX.Element {
		return (
			<AkContextualSurvey
				onDismiss={this.onDismiss}
				getUserHasAnsweredMailingList={this.getUserHasAnsweredMailingList}
				onMailingListAnswer={this.onMailingListAnswer}
				onSubmit={this.onSubmit}
				{...this.props}
			/>
		);
	}
}
